import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../auth';
import { formatMoney } from '../../../shared/money';

/**
 * Admin user-support surface (phase5 area 7).
 *
 * Search a user by email/username/name, then inspect everything support needs
 * in one place: profile state (incl. anonymization and restrictions), money
 * (payments/payouts/purchases with Stripe ids as dashboard link-outs only —
 * no card data exists anywhere), packs, deletion/refund request state, and
 * recent moderation + admin audit events.
 *
 * Dangerous actions (pack withdraw/restore) run through a dialog with a
 * MANDATORY audit note and a "notify user by email" checkbox (default ON for
 * withdrawal, OFF for restore). Other actions link out to their existing
 * admin surfaces.
 */

interface SearchProfile {
  id: string;
  displayName: string;
  username: string | null;
  isModel: boolean;
  isArtist: boolean;
  isHost: boolean;
  applicationStatus: string | null;
}

interface Overview {
  profile: {
    id: string;
    userId: string;
    displayName: string;
    username: string | null;
    country: string | null;
    isModel: boolean;
    isArtist: boolean;
    isHost: boolean;
    applicationStatus: string | null;
    anonymizedAt: string | null;
    platformFeeWaived: boolean;
    stripeAccountId: string | null;
    stripeOnboardingComplete: boolean;
    stripeCustomerId: string | null;
    createdAt: string;
  };
  account: { email: string | null; emailVerified: boolean; createdAt: string | null } | null;
  restrictions: Array<{
    id: string;
    restrictionType: string;
    enforcementStatus: string;
    reason: string | null;
    createdAt: string;
    liftedAt: string | null;
  }>;
  payments: {
    total: number;
    recent: Array<{
      id: string;
      roomCode: string | null;
      roomName: string | null;
      amount: number;
      currency: string | null;
      status: string;
      stripePaymentIntentId: string | null;
      stripeCheckoutSessionId: string | null;
      stripeRefundId: string | null;
      createdAt: string;
    }>;
  };
  payouts: {
    total: number;
    recent: Array<{
      id: string;
      roomCode: string | null;
      roomName: string | null;
      recipientRole: string;
      amount: number;
      currency: string | null;
      status: string;
      stripeTransferId: string | null;
      createdAt: string;
    }>;
  };
  purchases: {
    total: number;
    recent: Array<{
      id: string;
      productId: string;
      productTitle: string | null;
      amount: number;
      currency: string;
      status: string;
      stripePaymentIntentId: string | null;
      stripeCheckoutSessionId: string | null;
      revokedAt: string | null;
      createdAt: string;
    }>;
  };
  packs: Array<{
    id: string;
    title: string;
    status: string;
    priceCents: number | null;
    currency: string;
    unpublishedAt: string | null;
    withdrawnAt: string | null;
    completedPurchaseCount: number;
  }>;
  deletionRequest: {
    id: string;
    status: string;
    executionStatus: string;
    requestedAt: string;
    executedAt: string | null;
    lastExecutionError: string | null;
  } | null;
  refundRequests: {
    total: number;
    recent: Array<{
      id: string;
      status: string;
      sourceType: string;
      amountCents: number | null;
      currency: string | null;
      reason: string | null;
      roomCode: string | null;
      roomName: string | null;
      requestedAt: string;
      resolutionOutcome: string | null;
    }>;
  };
  reputationActions: Array<{
    id: string;
    actionType: string;
    sourceType: string;
    reason: string | null;
    createdAt: string;
  }>;
  auditEvents: Array<{
    id: string;
    action: string;
    subjectType: string;
    subjectId: string;
    reason: string | null;
    previousStatus: string | null;
    nextStatus: string | null;
    createdAt: string;
  }>;
}

type PackAction = {
  kind: 'withdraw' | 'restore';
  packId: string;
  packTitle: string;
};

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Stripe ids are exposed as dashboard link-outs only. Checkout session ids
 * have no stable dashboard URL, so they render as plain ids.
 */
function stripeUrl(kind: 'payment' | 'refund' | 'transfer' | 'account' | 'customer', id: string): string {
  switch (kind) {
    case 'payment': return `https://dashboard.stripe.com/payments/${id}`;
    case 'refund': return `https://dashboard.stripe.com/refunds/${id}`;
    case 'transfer': return `https://dashboard.stripe.com/connect/transfers/${id}`;
    case 'account': return `https://dashboard.stripe.com/connect/accounts/${id}`;
    case 'customer': return `https://dashboard.stripe.com/customers/${id}`;
  }
}

function StripeLink({ kind, id }: { kind: 'payment' | 'refund' | 'transfer' | 'account' | 'customer'; id: string | null }) {
  if (!id) return <span className="text-text-faint">—</span>;
  return (
    <a
      data-testid="stripe-link"
      href={stripeUrl(kind, id)}
      target="_blank"
      rel="noreferrer"
      className="font-mono text-xs text-accent hover:underline break-all"
    >
      {id}
    </a>
  );
}

function Chip({ label, tone }: { label: string; tone: 'ok' | 'warn' | 'dim' }) {
  const tones = {
    ok: 'border-ok/20 bg-ok/10 text-ok',
    warn: 'border-warn/20 bg-warn/10 text-warn',
    dim: 'border-line bg-panel2 text-text-dim',
  };
  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs ${tones[tone]}`}>{label}</span>
  );
}

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-line bg-panel2 px-4 py-2.5">
        <h2 className="text-[11px] font-mono uppercase text-text-faint font-semibold">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function UserSupportPage() {
  const { getToken } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SearchProfile[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [packAction, setPackAction] = useState<PackAction | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionNotify, setActionNotify] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);

  async function authedFetch(path: string, init?: RequestInit) {
    const token = await getToken();
    const headers = new Headers(init?.headers);
    if (init?.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return fetch(path, {
      ...init,
      headers,
    });
  }

  async function runSearch(event?: React.FormEvent) {
    event?.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const response = await authedFetch(`/api/admin/profiles?search=${encodeURIComponent(search.trim())}&limit=20`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Search failed');
      setResults(data.profiles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  }

  async function loadOverview(profileId: string) {
    setLoadingOverview(true);
    setError(null);
    try {
      const response = await authedFetch(`/api/admin/users/${profileId}/overview`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load user overview');
      setOverview(data.overview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user overview');
    } finally {
      setLoadingOverview(false);
    }
  }

  function openPackAction(action: PackAction) {
    setPackAction(action);
    setActionReason('');
    // Default ON for withdrawal (dangerous, user-facing), OFF for restore
    // (internal correction) — per the approved phase5 policy.
    setActionNotify(action.kind === 'withdraw');
  }

  async function confirmPackAction() {
    if (!packAction || !actionReason.trim() || !overview) return;
    setActionBusy(true);
    setError(null);
    try {
      const response = await authedFetch(`/api/admin/reference-packs/${packAction.packId}/${packAction.kind}`, {
        method: 'POST',
        body: JSON.stringify({ reason: actionReason.trim(), notifyUser: actionNotify }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to ${packAction.kind} pack`);
      setPackAction(null);
      await loadOverview(overview.profile.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${packAction.kind} pack`);
    } finally {
      setActionBusy(false);
    }
  }

  const profile = overview?.profile;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">User Support</h1>
        <p className="mt-1 text-sm text-text-dim">
          Find a user by email, username, or name and inspect their account, money, packs, and request state in one place.
          Stripe ids open in the Stripe dashboard; card data never reaches this system.
        </p>
      </div>

      <form onSubmit={runSearch} className="flex gap-2">
        <input
          type="text"
          data-testid="user-support-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Email, username, or display name"
          className="input w-full max-w-md"
        />
        <button type="submit" data-testid="user-support-search-submit" disabled={searching || !search.trim()} className="btn btn-accent disabled:opacity-50">
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div role="alert" className="rounded-lg border border-warn/30 bg-warn/10 px-4 py-3 text-sm text-warn">
          {error}
        </div>
      )}

      {results !== null && !overview && (
        <div className="rounded-xl border border-line bg-panel overflow-hidden">
          {results.length === 0 ? (
            <div className="p-4 text-sm text-text-dim">No matching users.</div>
          ) : (
            <ul className="divide-y divide-line">
              {results.map((result) => (
                <li key={result.id}>
                  <button
                    type="button"
                    data-testid={`user-support-result-${result.id}`}
                    onClick={() => loadOverview(result.id)}
                    className="w-full px-4 py-3 text-left hover:bg-panel2 transition-colors"
                  >
                    <span className="font-medium text-text">{result.displayName}</span>
                    {result.username && <span className="ml-2 text-xs text-text-faint">@{result.username}</span>}
                    <span className="ml-2 text-xs text-text-dim">
                      {[result.isModel && 'model', result.isArtist && 'artist', result.isHost && 'host'].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loadingOverview && <div className="text-sm text-text-dim">Loading user overview…</div>}

      {overview && profile && (
        <div className="space-y-4" data-testid="user-support-overview">
          <button
            type="button"
            onClick={() => setOverview(null)}
            className="text-xs text-text-dim hover:text-text"
          >
            ← Back to search results
          </button>

          <Section
            title="Profile"
            action={
              <Link to="/admin/profiles" className="text-xs text-accent hover:underline">Open profile admin</Link>
            }
          >
            <div data-testid="user-support-profile" className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-medium text-text">{profile.displayName}</span>
                {profile.username && <span className="text-sm text-text-faint">@{profile.username}</span>}
                {profile.anonymizedAt && <Chip label={`Anonymized ${formatDate(profile.anonymizedAt)}`} tone="warn" />}
                {profile.applicationStatus && <Chip label={`Application: ${profile.applicationStatus}`} tone={profile.applicationStatus === 'approved' ? 'ok' : 'dim'} />}
                {profile.isModel && <Chip label="Model" tone="dim" />}
                {profile.isArtist && <Chip label="Artist" tone="dim" />}
                {profile.isHost && <Chip label="Host" tone="dim" />}
                {profile.platformFeeWaived && <Chip label="Platform fee waived" tone="ok" />}
              </div>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
                <div className="flex gap-2"><dt className="text-text-faint">Email:</dt><dd className="text-text">{overview.account?.email ?? 'No auth account'}</dd></div>
                <div className="flex gap-2"><dt className="text-text-faint">Email verified:</dt><dd className="text-text">{overview.account ? (overview.account.emailVerified ? 'Yes' : 'No') : '—'}</dd></div>
                <div className="flex gap-2"><dt className="text-text-faint">Country:</dt><dd className="text-text">{profile.country ?? '—'}</dd></div>
                <div className="flex gap-2"><dt className="text-text-faint">Joined:</dt><dd className="text-text">{formatDate(profile.createdAt)}</dd></div>
                <div className="flex gap-2"><dt className="text-text-faint">Stripe account:</dt><dd><StripeLink kind="account" id={profile.stripeAccountId} /></dd></div>
                <div className="flex gap-2"><dt className="text-text-faint">Stripe customer:</dt><dd><StripeLink kind="customer" id={profile.stripeCustomerId} /></dd></div>
              </dl>
              {overview.restrictions.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-mono uppercase text-text-faint">Restrictions</div>
                  {overview.restrictions.map((restriction) => (
                    <div key={restriction.id} className="text-sm text-text">
                      <Chip label={`${restriction.restrictionType} · ${restriction.enforcementStatus}`} tone={restriction.liftedAt ? 'dim' : 'warn'} />
                      <span className="ml-2 text-text-dim">{restriction.reason ?? ''}</span>
                      <span className="ml-2 text-xs text-text-faint">{formatDate(restriction.createdAt)}{restriction.liftedAt ? ` · lifted ${formatDate(restriction.liftedAt)}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>

          <Section
            title={`Reference packs (${overview.packs.length})`}
            action={<Link to="/admin/reference-packs" className="text-xs text-accent hover:underline">Open packs admin</Link>}
          >
            {overview.packs.length === 0 ? (
              <div className="text-sm text-text-dim">No reference packs.</div>
            ) : (
              <div className="space-y-2">
                {overview.packs.map((pack) => (
                  <div key={pack.id} data-testid={`user-support-pack-${pack.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-text">{pack.title}</span>
                      <Chip
                        label={pack.status}
                        tone={pack.status === 'ready' ? 'ok' : pack.status === 'withdrawn' ? 'warn' : 'dim'}
                      />
                      <span className="text-xs text-text-dim">
                        {pack.priceCents === null ? 'Unknown' : formatMoney(pack.priceCents, pack.currency)}
                      </span>
                      <span className="text-xs text-text-faint">{pack.completedPurchaseCount} buyer{pack.completedPurchaseCount === 1 ? '' : 's'}</span>
                      {pack.withdrawnAt && <span className="text-xs text-text-faint">withdrawn {formatDate(pack.withdrawnAt)}</span>}
                      {!pack.withdrawnAt && pack.unpublishedAt && <span className="text-xs text-text-faint">unpublished {formatDate(pack.unpublishedAt)}</span>}
                    </div>
                    <div className="flex gap-2">
                      {(pack.status === 'ready' || pack.status === 'unpublished') && (
                        <button
                          type="button"
                          data-testid={`pack-withdraw-${pack.id}`}
                          onClick={() => openPackAction({ kind: 'withdraw', packId: pack.id, packTitle: pack.title })}
                          className="btn btn-sm"
                        >
                          Withdraw
                        </button>
                      )}
                      {pack.status === 'withdrawn' && (
                        <button
                          type="button"
                          data-testid={`pack-restore-${pack.id}`}
                          onClick={() => openPackAction({ kind: 'restore', packId: pack.id, packTitle: pack.title })}
                          className="btn btn-sm"
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title={`Payments (${overview.payments.total})`}>
            {overview.payments.recent.length === 0 ? (
              <div className="text-sm text-text-dim">No payments.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-faint">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Event</th>
                    <th className="py-1 pr-4 font-medium">Amount</th>
                    <th className="py-1 pr-4 font-medium">Status</th>
                    <th className="py-1 pr-4 font-medium">Stripe payment</th>
                    <th className="py-1 pr-4 font-medium">Stripe refund</th>
                    <th className="py-1 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {overview.payments.recent.map((payment) => (
                    <tr key={payment.id} data-testid={`user-support-payment-${payment.id}`}>
                      <td className="py-2 pr-4 text-text">{payment.roomName ?? '—'}{payment.roomCode ? <span className="text-xs text-text-faint"> /{payment.roomCode}</span> : null}</td>
                      <td className="py-2 pr-4 text-text">{formatMoney(payment.amount, payment.currency)}</td>
                      <td className="py-2 pr-4 text-text-dim">{payment.status}</td>
                      <td className="py-2 pr-4"><StripeLink kind="payment" id={payment.stripePaymentIntentId} /></td>
                      <td className="py-2 pr-4"><StripeLink kind="refund" id={payment.stripeRefundId} /></td>
                      <td className="py-2 text-text-dim">{formatDate(payment.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title={`Payouts (${overview.payouts.total})`}>
            {overview.payouts.recent.length === 0 ? (
              <div className="text-sm text-text-dim">No payouts.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-faint">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Event</th>
                    <th className="py-1 pr-4 font-medium">Role</th>
                    <th className="py-1 pr-4 font-medium">Amount</th>
                    <th className="py-1 pr-4 font-medium">Status</th>
                    <th className="py-1 pr-4 font-medium">Stripe transfer</th>
                    <th className="py-1 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {overview.payouts.recent.map((payout) => (
                    <tr key={payout.id} data-testid={`user-support-payout-${payout.id}`}>
                      <td className="py-2 pr-4 text-text">{payout.roomName ?? '—'}</td>
                      <td className="py-2 pr-4 text-text-dim">{payout.recipientRole}</td>
                      <td className="py-2 pr-4 text-text">{formatMoney(payout.amount, payout.currency)}</td>
                      <td className="py-2 pr-4 text-text-dim">{payout.status}</td>
                      <td className="py-2 pr-4"><StripeLink kind="transfer" id={payout.stripeTransferId} /></td>
                      <td className="py-2 text-text-dim">{formatDate(payout.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title={`Pack purchases (${overview.purchases.total})`}>
            {overview.purchases.recent.length === 0 ? (
              <div className="text-sm text-text-dim">No pack purchases.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-faint">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Pack</th>
                    <th className="py-1 pr-4 font-medium">Amount</th>
                    <th className="py-1 pr-4 font-medium">Status</th>
                    <th className="py-1 pr-4 font-medium">Stripe payment</th>
                    <th className="py-1 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {overview.purchases.recent.map((purchase) => (
                    <tr key={purchase.id} data-testid={`user-support-purchase-${purchase.id}`}>
                      <td className="py-2 pr-4 text-text">
                        {purchase.productTitle ?? '—'}
                        {purchase.revokedAt && <Chip label="access revoked" tone="warn" />}
                      </td>
                      <td className="py-2 pr-4 text-text">{formatMoney(purchase.amount, purchase.currency)}</td>
                      <td className="py-2 pr-4 text-text-dim">{purchase.status}</td>
                      <td className="py-2 pr-4"><StripeLink kind="payment" id={purchase.stripePaymentIntentId} /></td>
                      <td className="py-2 text-text-dim">{formatDate(purchase.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section
            title="Account deletion"
            action={
              <Link data-testid="user-support-deletion-link" to="/admin/account-deletion" className="text-xs text-accent hover:underline">
                Open deletion requests
              </Link>
            }
          >
            {overview.deletionRequest ? (
              <div data-testid="user-support-deletion" className="flex flex-wrap items-center gap-2 text-sm">
                <Chip
                  label={`Request ${overview.deletionRequest.status}`}
                  tone={overview.deletionRequest.status === 'pending' ? 'warn' : 'dim'}
                />
                <Chip label={`Execution: ${overview.deletionRequest.executionStatus}`} tone="dim" />
                <span className="text-text-dim">requested {formatDate(overview.deletionRequest.requestedAt)}</span>
                {overview.deletionRequest.executedAt && <span className="text-text-dim">executed {formatDate(overview.deletionRequest.executedAt)}</span>}
                {overview.deletionRequest.lastExecutionError && (
                  <span className="text-warn">Last error: {overview.deletionRequest.lastExecutionError}</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-text-dim">No deletion request.</div>
            )}
          </Section>

          <Section
            title={`Refund requests (${overview.refundRequests.total})`}
            action={
              <Link data-testid="user-support-refunds-link" to="/admin/refunds" className="text-xs text-accent hover:underline">
                Open refund queue
              </Link>
            }
          >
            {overview.refundRequests.recent.length === 0 ? (
              <div className="text-sm text-text-dim">No refund requests.</div>
            ) : (
              <div className="space-y-2">
                {overview.refundRequests.recent.map((request) => (
                  <div key={request.id} data-testid={`user-support-refund-${request.id}`} className="flex flex-wrap items-center gap-2 text-sm">
                    <Chip label={request.status} tone={request.status === 'pending' ? 'warn' : 'ok'} />
                    <span className="text-text">
                      {request.amountCents === null ? 'Unknown' : formatMoney(request.amountCents, request.currency)}
                    </span>
                    <span className="text-text-dim">{request.roomName ?? request.sourceType}</span>
                    <span className="text-xs text-text-faint">{formatDate(request.requestedAt)}</span>
                    {request.resolutionOutcome && <span className="text-xs text-text-faint">→ {request.resolutionOutcome}</span>}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section
            title={`Moderation actions (${overview.reputationActions.length})`}
            action={<Link to="/admin/moderation" className="text-xs text-accent hover:underline">Open moderation</Link>}
          >
            {overview.reputationActions.length === 0 ? (
              <div className="text-sm text-text-dim">No moderation actions.</div>
            ) : (
              <div className="space-y-1">
                {overview.reputationActions.map((action) => (
                  <div key={action.id} className="text-sm text-text">
                    <span className="font-mono text-xs text-text-dim">{action.actionType}</span>
                    <span className="ml-2 text-text-dim">{action.reason ?? ''}</span>
                    <span className="ml-2 text-xs text-text-faint">{formatDate(action.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title={`Recent admin audit events (${overview.auditEvents.length})`}>
            {overview.auditEvents.length === 0 ? (
              <div className="text-sm text-text-dim">No audit events.</div>
            ) : (
              <div className="space-y-1" data-testid="user-support-audit">
                {overview.auditEvents.map((event) => (
                  <div key={event.id} className="text-sm">
                    <span className="font-mono text-xs text-text-dim">{event.action}</span>
                    {event.previousStatus && event.nextStatus && (
                      <span className="ml-2 text-xs text-text-faint">{event.previousStatus} → {event.nextStatus}</span>
                    )}
                    <span className="ml-2 text-text">{event.reason ?? ''}</span>
                    <span className="ml-2 text-xs text-text-faint">{formatDate(event.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {packAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => !actionBusy && setPackAction(null)} />
          <div className="relative w-full max-w-md rounded-xl border border-line bg-panel p-5 space-y-4">
            <h2 className="text-lg font-medium text-text">
              {packAction.kind === 'withdraw' ? 'Withdraw reference pack' : 'Restore reference pack'}
            </h2>
            <p className="text-sm text-text-dim">
              {packAction.kind === 'withdraw'
                ? `"${packAction.packTitle}" will be hidden from everyone, including buyers. Nothing is deleted and no purchases are refunded.`
                : `"${packAction.packTitle}" will be available again and buyers regain access.`}
            </p>
            <label className="block">
              <span className="block text-[10px] font-mono text-text-faint uppercase mb-1">Audit note (required)</span>
              <textarea
                data-testid="support-action-reason"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Why is this action being taken? Recorded in the audit log."
                className="input w-full h-20 resize-none"
              />
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="support-action-notify"
                checked={actionNotify}
                onChange={(e) => setActionNotify(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-text">Notify user by email</span>
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="support-action-cancel"
                onClick={() => setPackAction(null)}
                disabled={actionBusy}
                className="btn"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="support-action-confirm"
                onClick={confirmPackAction}
                disabled={actionBusy || !actionReason.trim()}
                className="btn btn-accent disabled:opacity-50"
              >
                {actionBusy ? 'Working…' : packAction.kind === 'withdraw' ? 'Withdraw pack' : 'Restore pack'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
